import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Switch,
  IconButton,
  Autocomplete,
  Paper,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Stack,
  Chip,
  Tooltip,
  FormControlLabel,
} from '@mui/material';
import MoneyIcon from '@mui/icons-material/Money';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import AddIcon from '@mui/icons-material/Add';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import PavoPaySettingsModal from './PavoPaySettingsModal';
import PavoP2PDevicesModal from './PavoP2PDevicesModal';
import PavoRestP2PDeviceSelectDialog from './PavoRestP2PDeviceSelectDialog';
import IngenicoDeviceDepartmentsModal from '../components/modals/IngenicoDeviceDepartmentsModal';
import RouterIcon from '@mui/icons-material/Router';
import { pavoApiService, type PavoDevice } from '../../../../services/pavoApiService';
import { useTranslation } from 'react-i18next';

// ✅ YENİ: Diğer ödeme yöntemleri için hardcoded seçenekler
// (export: AI ajan köprüsü de geçerli "diğer ödeme" adlarını bu listeden doğrular)
export const OTHER_PAYMENT_OPTIONS = [
  'TOKENFLEX',
  'EDENRED',
  'MULTINET',
  'SETCARD',
  'PLUXEE',
  'METROPOL',
  'TICKET',
  'PAYE',
  'FIRSAT ME',
  'BU BİLET'
];

// ✅ YENİ: Online ödeme yöntemleri için hardcoded seçenekler
const ONLINE_PAYMENT_OPTIONS = [
  'YEMEK SEPETİ',
  'GETİR YEMEK',
  'TRENDYOL YEMEK',
  'MİGROS YEMEK'
];

// Mevcut PaymentOption tipini QuickSaleScreenSettingsModal'dan alalım
export type PaymentOptionType = 'cash' | 'pos' | 'openAccount' | 'voucher' | 'receipt' | 'bonus' | 'accrual' | 'other' | 'online';

export interface PaymentOption {
  id: string;
  type: PaymentOptionType;
  label: string;
  enabled: boolean;
  cashboxId?: string | null;
  cashboxName?: string | null; // ✅ YENİ: Kasa adı (gün sonu raporu için)
  bankAccountId?: string | null;
  bankAccountName?: string | null; // ✅ YENİ: Banka adı (gün sonu raporu için)
  defaultAccountId?: string | null;
  timestamp?: number;
  subAccountId?: string | null;
  relatedAccount?: string;
  accountId?: string | null; // ✅ YENİ: Diğer ödeme için cari hesap ID
  accountName?: string | null; // ✅ YENİ: Diğer ödeme için cari hesap adı
  // ✅ YENİ: Online ödeme için ek alanlar (3 farklı ödeme yöntemi)
  onlineCashboxId?: string | null; // Nakit kasa ID
  onlineCashboxName?: string | null; // Nakit kasa adı
  onlineBankAccountId?: string | null; // POS banka hesap ID
  onlineBankAccountName?: string | null; // POS banka hesap adı
  onlineSubAccountId?: string | null; // POS alt hesap ID
  // ✅ YENİ: Online buton için (şimdilik nakit kasasına kayıt edilecek)
  onlineDirectCashboxId?: string | null; // Online buton için kasa ID
  onlineDirectCashboxName?: string | null; // Online buton için kasa adı
}

// Kasa, hesap ve banka hesapları tipleri
interface Cashbox {
  id: string;
  name: string;
}

interface Account {
  id: string;
  companyName: string;
}

interface BankAccount {
  id: string;
  bankName: string;
  accountNumber: string;
  isPosAccount?: boolean;
  subAccounts?: { [key: string]: SubAccountForModal };
}

interface SubAccountForModal {
  id: string;
  accountName: string;
  accountNumber: string;
  isPosAccount?: boolean;
}

// ✅ Yazarkasa markaları listesi
const FISCAL_DEVICE_OPTIONS = [
  { id: 'hugin_t300', label: 'Hugin T300' },
  { id: 'inpos_m530', label: 'Inpos M530' },
  { id: 'beko_x30tr', label: 'Beko X30TR' },
  { id: 'beko_300tr', label: 'Beko 300TR' },
  { id: 'pavopay', label: 'PavoPay' },
  { id: 'ingenico_5000f', label: 'Ingenico 5000F' },
];

// Sekme için props arayüzü
interface PaymentSettingsTabProps {
  paymentOptions: PaymentOption[];
  allCashboxes: Cashbox[];
  allAccounts: Account[];
  allBankAccounts: BankAccount[];
  loading: boolean;
  prepaidSystemEnabled: boolean; // Ön ödemeli sistem aktif mi?
  onPaymentOptionFieldChange: (id: string, field: keyof PaymentOption, value: any) => void;
  onAddPaymentOption: (type: PaymentOptionType) => void;
  onDeletePaymentOption: (idToDelete: string) => void;
  onPrepaidSystemChange: (enabled: boolean) => void; // Ön ödemeli sistem durumu değişikliği
  useFiscalDeviceForCash: boolean;
  onUseFiscalDeviceForCashChange: (enabled: boolean) => void;
  selectedFiscalDevice: string; // Seçili yazarkasa markası
  onFiscalDeviceChange: (device: string) => void;
  warnIfFiscalNotConnected: boolean;
  onWarnIfFiscalNotConnectedChange: (enabled: boolean) => void; // ✅ YENİ: Uyarı ayarı değişikliği
  showCashChangeModal: boolean; // ✅ YENİ: Para üstü modalı
  onShowCashChangeModalChange: (enabled: boolean) => void;
  usePavoPayForOtherPayments: boolean;
  onUsePavoPayForOtherPaymentsChange: (enabled: boolean) => void;
  useExternalPrinterForPavo: boolean;
  onUseExternalPrinterForPavoChange: (enabled: boolean) => void;
  // ✅ YENİ: Rest-P2P modu ayarları
  useRestP2PForPayments: boolean;
  onUseRestP2PForPaymentsChange: (enabled: boolean) => void;
  pavoRestP2PDeviceScreenCode: string | null;
  pavoRestP2PDeviceName?: string | null;
  onPavoRestP2PDeviceChange: (device: { code: string; name: string } | null) => void;
  companyId?: string;
  branchId?: string;
}

const PaymentSettingsTab: React.FC<PaymentSettingsTabProps> = ({
  paymentOptions,
  allCashboxes,
  allAccounts,
  allBankAccounts,
  loading,
  prepaidSystemEnabled,
  onPaymentOptionFieldChange,
  onAddPaymentOption,
  onDeletePaymentOption,
  onPrepaidSystemChange,
  useFiscalDeviceForCash,
  onUseFiscalDeviceForCashChange,
  selectedFiscalDevice,
  onFiscalDeviceChange,
  warnIfFiscalNotConnected,
  onWarnIfFiscalNotConnectedChange,
  showCashChangeModal,
  onShowCashChangeModalChange,
  usePavoPayForOtherPayments,
  onUsePavoPayForOtherPaymentsChange,
  useExternalPrinterForPavo,
  onUseExternalPrinterForPavoChange,
  useRestP2PForPayments,
  onUseRestP2PForPaymentsChange,
  pavoRestP2PDeviceScreenCode,
  pavoRestP2PDeviceName,
  onPavoRestP2PDeviceChange,
  companyId,
  branchId,
}) => {
  const { t } = useTranslation('salesSystem');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // ✅ YENİ: PavoPay ayarlar modalı için state
  const [pavoPaySettingsModalOpen, setPavoPaySettingsModalOpen] = useState(false);
  const [ingenicoDeviceDeptModalOpen, setIngenicoDeviceDeptModalOpen] = useState(false);
  const [pavoP2pModalOpen, setPavoP2pModalOpen] = useState(false);
  // ✅ YENİ: Rest-P2P cihaz seçim dialogu
  const [restP2PSelectOpen, setRestP2PSelectOpen] = useState(false);
  // ✅ YENİ: Cihaz kodu varken ismi bilinmiyorsa arka planda çözümle
  const [resolvedRestP2PDeviceName, setResolvedRestP2PDeviceName] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    const resolve = async () => {
      if (!pavoRestP2PDeviceScreenCode) {
        setResolvedRestP2PDeviceName(null);
        return;
      }
      if (pavoRestP2PDeviceName) {
        setResolvedRestP2PDeviceName(pavoRestP2PDeviceName);
        return;
      }
      try {
        const device = await pavoApiService.getPavoDevice(pavoRestP2PDeviceScreenCode);
        if (!cancelled) {
          setResolvedRestP2PDeviceName(device?.deviceName || null);
        }
      } catch (e) {
        console.warn('[PaymentSettingsTab] Rest-P2P cihaz ismi çözümlenemedi:', e);
        if (!cancelled) setResolvedRestP2PDeviceName(null);
      }
    };
    resolve();
    return () => {
      cancelled = true;
    };
  }, [pavoRestP2PDeviceScreenCode, pavoRestP2PDeviceName]);
  
  // SODEXO -> PLUXEE otomatik güncelleme
  React.useEffect(() => {
    paymentOptions.forEach(option => {
      if (option.type === 'other' && option.label?.toUpperCase() === 'SODEXO') {
        onPaymentOptionFieldChange(option.id, 'label', 'PLUXEE');
      }
    });
  }, [paymentOptions, onPaymentOptionFieldChange]);

  // Bu sekmede kullanılacak helper fonksiyonlar
  const cashOptionExists = paymentOptions.some(option => option.type === 'cash');
  const openAccountOptionExists = paymentOptions.some(option => option.type === 'openAccount');

  // Debug için banka hesaplarını konsola yazdıralım
  React.useEffect(() => {
    console.log("Tüm banka hesapları:", allBankAccounts);
  }, [allBankAccounts]);

  return (
    <Paper elevation={0} sx={{ p: 1.5 }}>
      {/* Yazarkasa Seçimi - Dropdown ve Uyarı Ayarı */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          {t('paymentSettingsTab.fiscalSettings')}
        </Typography>
        {/* Üst satır: Yazarkasa seçimi + ayar butonu + bağlantı uyarısı */}
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          alignItems: 'center',
          flexWrap: { xs: 'wrap', md: 'nowrap' }
        }}>
          {/* Yazarkasa Marka Seçimi - Dropdown */}
          <Autocomplete
            size="small"
            options={FISCAL_DEVICE_OPTIONS}
            getOptionLabel={(option) => option.label}
            value={FISCAL_DEVICE_OPTIONS.find(opt => opt.id === selectedFiscalDevice) || FISCAL_DEVICE_OPTIONS[0]}
            onChange={(e, newValue) => {
              if (newValue) {
                onFiscalDeviceChange(newValue.id);
              }
            }}
            renderInput={(params) => (
              <TextField 
                {...params} 
                variant="outlined" 
                label={t('paymentSettingsTab.fiscalBrand')}
                size="small"
              />
            )}
            sx={{ minWidth: { xs: '100%', md: 200 } }}
            disableClearable
          />
          
          {/* PavoPay Ayarlar Butonu */}
          {selectedFiscalDevice === 'pavopay' && (
            <Tooltip title={t('paymentSettingsTab.pavoPaySettingsTooltip')}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<SettingsIcon />}
                onClick={() => setPavoPaySettingsModalOpen(true)}
                sx={{
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                    borderColor: 'primary.dark',
                  }
                }}
              >
                {t('paymentSettingsTab.pavoPaySettings')}
              </Button>
            </Tooltip>
          )}

          {selectedFiscalDevice === 'pavopay' && (
            <Tooltip title={t('paymentSettingsTab.p2pDevicesTooltip')}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PointOfSaleIcon />}
                onClick={() => setPavoP2pModalOpen(true)}
                sx={{
                  borderColor: '#7c4dff',
                  color: '#7c4dff',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    backgroundColor: 'rgba(124,77,255,0.08)',
                    borderColor: '#651fff',
                  }
                }}
              >
                {t('paymentSettingsTab.p2pDevices')}
              </Button>
            </Tooltip>
          )}

          {/* Ingenico KDV/Departman Tablosu Butonu */}
          {selectedFiscalDevice === 'ingenico_5000f' && (
            <Tooltip title={t('paymentSettingsTab.vatDepartmentTooltip')}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<SettingsIcon />}
                onClick={() => setIngenicoDeviceDeptModalOpen(true)}
                sx={{
                  borderColor: '#e65100',
                  color: '#e65100',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    backgroundColor: '#fff3e0',
                    borderColor: '#bf360c',
                  }
                }}
              >
                {t('paymentSettingsTab.vatDepartmentTable')}
              </Button>
            </Tooltip>
          )}
          
          {/* Yazarkasa Bağlantı Uyarısı Switch */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            border: '1px solid',
            borderColor: warnIfFiscalNotConnected ? 'warning.main' : 'divider',
            borderRadius: 1,
            px: 1.5,
            py: 0.5,
            bgcolor: warnIfFiscalNotConnected ? 'warning.lighter' : 'transparent',
            transition: 'all 0.2s',
          }}>
            <Switch
              size="small"
              checked={warnIfFiscalNotConnected}
              onChange={(e) => onWarnIfFiscalNotConnectedChange(e.target.checked)}
            />
            <Typography 
              variant="body2" 
              sx={{ 
                ml: 0.5, 
                color: warnIfFiscalNotConnected ? 'warning.dark' : 'text.secondary',
                whiteSpace: 'nowrap',
                fontSize: { xs: '0.75rem', md: '0.875rem' }
              }}
            >
              {warnIfFiscalNotConnected ? t('paymentSettingsTab.connectionWarningOn') : t('paymentSettingsTab.connectionWarningOff')}
            </Typography>
          </Box>
        </Box>

        {/* Alt satır: PavoPay'e özel ayarlar
             NOT: Rest-P2P aktifken bu ayarlar ANLAMSIZ olduğu için gizlenir
             (fiscal + yazıcı işi tamamen Pavo cihazında yapılır) */}
        {selectedFiscalDevice === 'pavopay' && !useRestP2PForPayments && (
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            alignItems: 'center',
            flexWrap: 'wrap',
            mt: 1.5,
            pl: { md: 0 },
          }}>
            {/* Diğer Ödemelerde PavoPay Kullan */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              border: '1px solid',
              borderColor: usePavoPayForOtherPayments ? 'info.main' : 'divider',
              borderRadius: 1,
              px: 1.5,
              py: 0.5,
              bgcolor: usePavoPayForOtherPayments ? 'info.lighter' : 'transparent',
              transition: 'all 0.2s',
            }}>
              <Switch
                size="small"
                checked={usePavoPayForOtherPayments}
                onChange={(e) => onUsePavoPayForOtherPaymentsChange(e.target.checked)}
              />
              <Typography 
                variant="body2" 
                sx={{ 
                  ml: 0.5, 
                  color: usePavoPayForOtherPayments ? 'info.dark' : 'text.secondary',
                  whiteSpace: 'nowrap',
                  fontSize: { xs: '0.75rem', md: '0.875rem' }
                }}
              >
                {usePavoPayForOtherPayments ? t('paymentSettingsTab.pavoPayForOtherPaymentsOn') : t('paymentSettingsTab.pavoPayForOtherPaymentsOff')}
              </Typography>
            </Box>

            {/* Harici Yazıcı Kullan */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              border: '1px solid',
              borderColor: useExternalPrinterForPavo ? 'warning.main' : 'divider',
              borderRadius: 1,
              px: 1.5,
              py: 0.5,
              bgcolor: useExternalPrinterForPavo ? 'warning.lighter' : 'transparent',
              transition: 'all 0.2s',
            }}>
              <Switch
                size="small"
                checked={useExternalPrinterForPavo}
                onChange={(e) => onUseExternalPrinterForPavoChange(e.target.checked)}
              />
              <Typography 
                variant="body2" 
                sx={{ 
                  ml: 0.5, 
                  color: useExternalPrinterForPavo ? 'warning.dark' : 'text.secondary',
                  whiteSpace: 'nowrap',
                  fontSize: { xs: '0.75rem', md: '0.875rem' }
                }}
              >
                {useExternalPrinterForPavo ? t('paymentSettingsTab.externalPrinterOn') : t('paymentSettingsTab.internalPrinterOn')}
              </Typography>
            </Box>
          </Box>
        )}

        {/* ✅ YENİ: Rest-P2P Modu Ayarları (sadece PavoPay seçiliyken) */}
        {selectedFiscalDevice === 'pavopay' && (
          <Box
            sx={{
              mt: 1.5,
              p: 1.5,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: useRestP2PForPayments ? '#7c4dff' : 'divider',
              bgcolor: useRestP2PForPayments ? 'rgba(124,77,255,0.04)' : 'transparent',
              transition: 'all 0.2s',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <RouterIcon sx={{ color: useRestP2PForPayments ? '#7c4dff' : 'text.disabled', fontSize: 20 }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: useRestP2PForPayments ? '#7c4dff' : 'text.primary' }}>
                    {t('paymentSettingsTab.restP2p.title')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('paymentSettingsTab.restP2p.description')}
                  </Typography>
                </Box>
              </Box>
              <Switch
                checked={useRestP2PForPayments}
                onChange={(e) => onUseRestP2PForPaymentsChange(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#7c4dff' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#7c4dff' },
                }}
              />
            </Box>

            {useRestP2PForPayments && (
              <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PointOfSaleIcon />}
                  onClick={() => setRestP2PSelectOpen(true)}
                  sx={{
                    borderColor: '#7c4dff',
                    color: '#7c4dff',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      backgroundColor: 'rgba(124,77,255,0.08)',
                      borderColor: '#651fff',
                    },
                  }}
                >
                  {pavoRestP2PDeviceScreenCode ? t('paymentSettingsTab.restP2p.changeDevice') : t('paymentSettingsTab.restP2p.selectDevice')}
                </Button>
                {pavoRestP2PDeviceScreenCode ? (
                  <Chip
                    icon={<PointOfSaleIcon sx={{ fontSize: '1rem !important' }} />}
                    label={
                      (pavoRestP2PDeviceName || resolvedRestP2PDeviceName)
                        ? `${pavoRestP2PDeviceName || resolvedRestP2PDeviceName} • ${pavoRestP2PDeviceScreenCode}`
                        : pavoRestP2PDeviceScreenCode
                    }
                    size="small"
                    onDelete={() => onPavoRestP2PDeviceChange(null)}
                    sx={{ bgcolor: 'rgba(124,77,255,0.12)', color: '#7c4dff', fontWeight: 600 }}
                  />
                ) : (
                  <Typography variant="caption" color="error">
                    {t('paymentSettingsTab.restP2p.deviceRequired')}
                  </Typography>
                )}
              </Box>
            )}
            {useRestP2PForPayments && (
              <Box sx={{ mt: 1.5, p: 1, borderRadius: 1, bgcolor: 'rgba(124,77,255,0.06)', border: '1px dashed rgba(124,77,255,0.4)' }}>
                <Typography variant="caption" sx={{ color: '#5e35b1', fontWeight: 500, display: 'block', lineHeight: 1.45 }}>
                  {t('paymentSettingsTab.restP2p.activeHint')}
                </Typography>
              </Box>
            )}
          </Box>
        )}
        
        {/* Açıklama metni */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {warnIfFiscalNotConnected 
            ? t('paymentSettingsTab.warningDescription.on') 
            : t('paymentSettingsTab.warningDescription.off')}
        </Typography>
      </Box>
      
      <Divider sx={{ mb: 2 }}/>
      
      {/* Ödeme Ayarları */}
      {isMobile ? (
        // 📱 MOBİL GÖRÜNÜM
        <Box sx={{ mb: 1.5 }}>
          {/* Üst Satır: Switch'ler */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Switch 
                size="small"
                checked={prepaidSystemEnabled}
                onChange={(e) => onPrepaidSystemChange(e.target.checked)}
              />
              <Typography variant="caption" sx={{ ml: 0.5 }}>
                {t('paymentSettingsTab.prepaidShort')}
              </Typography>
            </Box>
            {!useRestP2PForPayments && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Switch
                  size="small"
                  checked={useFiscalDeviceForCash}
                  onChange={(e) => onUseFiscalDeviceForCashChange(e.target.checked)}
                />
                <Typography 
                  variant="caption" 
                  sx={{ 
                    ml: 0.5, 
                    color: useFiscalDeviceForCash 
                      ? 'primary.main' 
                      : 'text.secondary' 
                  }}
                >
                  {useFiscalDeviceForCash ? t('paymentSettingsTab.fiscalShortOn') : t('paymentSettingsTab.fiscalShortOff')}
                </Typography>
              </Box>
            )}
          </Box>
          
          {/* Alt Satır: 3 Buton Yan Yana */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Button 
              size="small" 
              onClick={() => onAddPaymentOption('cash')} 
              variant="outlined" 
              disabled={cashOptionExists}
              sx={{ flex: 1, fontSize: '0.7rem', py: 1 }}
              startIcon={<MoneyIcon sx={{ fontSize: '1rem !important' }} />}
            >
              {t('paymentSettingsTab.paymentTypes.cash')}
            </Button>
            <Button 
              size="small" 
              onClick={() => onAddPaymentOption('pos')} 
              variant="outlined" 
              sx={{ flex: 1, fontSize: '0.7rem', py: 1 }}
              startIcon={<CreditCardIcon sx={{ fontSize: '1rem !important' }} />}
            >
              POS
            </Button>
            <Button
              size="small"
              onClick={() => onAddPaymentOption('openAccount')}
              variant="outlined"
              disabled={openAccountOptionExists}
              sx={{ flex: 1, fontSize: '0.7rem', py: 1 }}
              startIcon={<AccountBalanceIcon sx={{ fontSize: '1rem !important' }} />}
            >
              {t('paymentSettingsTab.paymentTypes.openAccountShort')}
            </Button>
          </Box>
        </Box>
      ) : (
        // 💻 MASAÜSTÜ GÖRÜNÜM
        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5}}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Switch 
                size="small"
                checked={prepaidSystemEnabled}
                onChange={(e) => onPrepaidSystemChange(e.target.checked)}
              />
              <Typography variant="body2" sx={{ ml: 0.5, mr: 2 }}>
                {t('paymentSettingsTab.prepaidEnable')}
              </Typography>
              {!useRestP2PForPayments && (
                <>
                  <Switch
                    size="small"
                    checked={useFiscalDeviceForCash}
                    onChange={(e) => onUseFiscalDeviceForCashChange(e.target.checked)}
                  />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      ml: 0.5, 
                      color: useFiscalDeviceForCash 
                        ? 'primary.main' 
                        : 'text.secondary' 
                    }}
                  >
                    {useFiscalDeviceForCash 
                      ? t('paymentSettingsTab.fiscalActive')
                      : t('paymentSettingsTab.fiscalInactive')
                    }
                  </Typography>
                </>
              )}
            </Box>
          </Box>
          {/* Para Üstü Modalı Ayarı */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            p: 1,
            borderRadius: 1,
            bgcolor: showCashChangeModal ? 'success.50' : 'action.hover',
            border: '1px solid',
            borderColor: showCashChangeModal ? 'success.light' : 'divider',
            mb: 1
          }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showCashChangeModal}
                  onChange={(e) => onShowCashChangeModalChange(e.target.checked)}
                  size="small"
                  color="success"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: showCashChangeModal ? 600 : 400 }}>
                  {showCashChangeModal ? t('paymentSettingsTab.cashChangeModalOn') : t('paymentSettingsTab.cashChangeModalOff')}
                </Typography>
              }
              sx={{ m: 0 }}
            />
          </Box>

          <Box>
            <Button 
              size="small" 
              onClick={() => onAddPaymentOption('cash')} 
              variant="outlined" 
              sx={{mr: 1}} 
              disabled={cashOptionExists}
            >
              {t('paymentSettingsTab.addCash')}
            </Button>
            <Button 
              size="small" 
              onClick={() => onAddPaymentOption('pos')} 
              variant="outlined" 
              sx={{mr: 1}}
            >
              {t('paymentSettingsTab.addPos')}
            </Button>
            <Button
              size="small"
              onClick={() => onAddPaymentOption('openAccount')}
              variant="outlined"
              disabled={openAccountOptionExists}
            >
              {t('paymentSettingsTab.addOpenAccount')}
            </Button>
            <Button
              size="small"
              onClick={() => onAddPaymentOption('other')}
              variant="outlined"
              sx={{ml: 1}}
              startIcon={<AddIcon />}
            >
              {t('paymentSettingsTab.addOtherPayment')}
            </Button>
            {/* Online Ödeme Ekle butonu şimdilik gizli - hızlı satışta kullanılmıyor */}
          </Box>
        </Box>
      )}
      <Divider sx={{ mb: 1.5 }}/>
      
      {paymentOptions.length === 0 && !loading ? (
        <Typography variant="caption" color="text.secondary">{t('paymentSettingsTab.noPaymentMethod')}</Typography>
      ) : isMobile ? (
        // 📱 MOBİL: Kart Görünümü
        <Stack spacing={1.5}>
          {paymentOptions.map((option) => {
            const usedCashboxIds = paymentOptions
              .filter(opt => opt.id !== option.id && opt.type === 'cash' && opt.cashboxId)
              .map(opt => opt.cashboxId);
            const usedBankAccountIds = paymentOptions
              .filter(opt => opt.id !== option.id && opt.type === 'pos' && opt.bankAccountId)
              .map(opt => opt.bankAccountId);
            
            let typeLabel = '';
            let TypeIcon = MoneyIcon;
            let typeColor = 'success.main';
            switch (option.type) {
              case 'cash': typeLabel = t('paymentSettingsTab.paymentTypes.cash'); TypeIcon = MoneyIcon; typeColor = 'success.main'; break;
              case 'pos': typeLabel = 'POS'; TypeIcon = CreditCardIcon; typeColor = 'primary.main'; break;
              case 'openAccount': typeLabel = t('paymentSettingsTab.paymentTypes.openAccount'); TypeIcon = AccountBalanceIcon; typeColor = 'warning.main'; break;
              case 'other': typeLabel = t('paymentSettingsTab.paymentTypes.other'); TypeIcon = MoreHorizIcon; typeColor = 'secondary.main'; break;
              case 'online': typeLabel = 'Online'; TypeIcon = CreditCardIcon; typeColor = 'info.main'; break;
              default: typeLabel = option.type;
            }
            
            return (
              <Card key={option.id} variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  {/* Üst Satır: Tip + Durum + Sil */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip 
                        icon={<TypeIcon sx={{ fontSize: '1rem !important' }} />}
                        label={typeLabel} 
                        size="small" 
                        sx={{ bgcolor: typeColor, color: 'white', fontWeight: 'bold' }}
                      />
                      <Switch
                        size="small"
                        checked={option.enabled}
                        onChange={(e) => onPaymentOptionFieldChange(option.id, 'enabled', e.target.checked)}
                      />
                      <Typography variant="caption" color={option.enabled ? 'success.main' : 'text.secondary'}>
                        {option.enabled ? t('paymentSettingsTab.status.active') : t('paymentSettingsTab.status.passive')}
                      </Typography>
                    </Box>
                    <IconButton size="small" color="error" onClick={() => onDeletePaymentOption(option.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  
                  {/* Etiket */}
                  {option.type === 'other' ? (
                    // ✅ YENİ: Diğer ödeme için hardcoded dropdown
                    <Autocomplete
                      size="small"
                      options={OTHER_PAYMENT_OPTIONS}
                      value={OTHER_PAYMENT_OPTIONS.includes(option.label) ? option.label : undefined}
                      onChange={(e, newValue) => onPaymentOptionFieldChange(option.id, 'label', newValue || '')}
                      renderInput={(params) => <TextField {...params} variant="outlined" size="small" label={t('paymentSettingsTab.labels.paymentType')} />}
                      sx={{ mb: 1 }}
                      fullWidth
                    />
                  ) : option.type === 'online' ? (
                    // ✅ YENİ: Online ödeme için hardcoded dropdown
                    <Autocomplete
                      size="small"
                      options={ONLINE_PAYMENT_OPTIONS}
                      value={ONLINE_PAYMENT_OPTIONS.includes(option.label) ? option.label : undefined}
                      onChange={(e, newValue) => onPaymentOptionFieldChange(option.id, 'label', newValue || '')}
                      renderInput={(params) => <TextField {...params} variant="outlined" size="small" label={t('paymentSettingsTab.labels.onlinePlatform')} />}
                      sx={{ mb: 1 }}
                      fullWidth
                    />
                  ) : (
                    <TextField 
                      variant="outlined"
                      size="small"
                      fullWidth
                      label={t('paymentSettingsTab.labels.label')}
                      value={option.label}
                      onChange={(e) => onPaymentOptionFieldChange(option.id, 'label', e.target.value)}
                      sx={{ mb: 1 }}
                    />
                  )}
                  
                  {/* Bağlantı */}
                  {option.type === 'cash' && (
                    <Autocomplete
                      size="small"
                      options={allCashboxes.filter(cb => 
                        !usedCashboxIds.includes(cb.id) || cb.id === option.cashboxId
                      )}
                      getOptionLabel={(cb) => cb.name}
                      value={allCashboxes.find(cb => cb.id === option.cashboxId) || null}
                      onChange={(e, newValue) => {
                        onPaymentOptionFieldChange(option.id, 'cashboxId', newValue?.id ?? null);
                        onPaymentOptionFieldChange(option.id, 'cashboxName', newValue?.name ?? null); // ✅ YENİ: Kasa adını da kaydet
                      }}
                      renderInput={(params) => <TextField {...params} variant="outlined" size="small" label={t('paymentSettingsTab.labels.selectCashbox')} />}
                      fullWidth
                    />
                  )}
                  {option.type === 'pos' && (
                    <Autocomplete
                      size="small"
                      options={[
                        ...allBankAccounts
                          .filter(ba => ba.isPosAccount && (!usedBankAccountIds.includes(ba.id) || ba.id === option.bankAccountId))
                          .map(ba => ({
                            id: ba.id,
                            bankName: `${ba.bankName} - ${ba.accountNumber}`,
                            accountNumber: ba.accountNumber,
                            isPosAccount: true,
                            isSubAccount: false,
                            mainAccountId: ba.id,
                            subAccountId: null
                          })),
                        ...allBankAccounts.flatMap(ba => 
                          ba.subAccounts ? 
                          Object.entries(ba.subAccounts)
                            .filter(([subId, sub]) => sub.isPosAccount)
                            .map(([subId, sub]) => ({ 
                              id: `${ba.id}_sub_${subId}`,
                              bankName: `${ba.bankName} - ${sub.accountName}`,
                              accountNumber: sub.accountNumber,
                              isPosAccount: true,
                              isSubAccount: true,
                              mainAccountId: ba.id,
                              subAccountId: subId
                            }))
                          : []
                        )
                      ]}
                      getOptionLabel={(opt: any) => opt.bankName}
                      value={(() => {
                        if (option.bankAccountId && option.subAccountId) {
                          const mainAcc = allBankAccounts.find(ba => ba.id === option.bankAccountId);
                          if (!mainAcc) return null;
                          const subAcc = mainAcc.subAccounts?.[option.subAccountId];
                          if (mainAcc && subAcc) {
                            return {
                              id: `${mainAcc.id}_sub_${option.subAccountId}`,
                              bankName: `${mainAcc.bankName} - ${subAcc.accountName}`,
                              accountNumber: subAcc.accountNumber,
                              isPosAccount: true,
                              isSubAccount: true,
                              mainAccountId: mainAcc.id,
                              subAccountId: option.subAccountId
                            };
                          }
                        } else if (option.bankAccountId) {
                          const mainAcc = allBankAccounts.find(ba => ba.id === option.bankAccountId);
                          if (mainAcc) {
                            return {
                              id: mainAcc.id,
                              bankName: `${mainAcc.bankName} - ${mainAcc.accountNumber}`,
                              accountNumber: mainAcc.accountNumber,
                              isPosAccount: true,
                              isSubAccount: false,
                              mainAccountId: mainAcc.id,
                              subAccountId: null
                            };
                          }
                        }
                        return null;
                      })()}
                      onChange={(e, newValue: any) => {
                        if (newValue) {
                          onPaymentOptionFieldChange(option.id, 'bankAccountId', newValue.mainAccountId);
                          onPaymentOptionFieldChange(option.id, 'subAccountId', newValue.isSubAccount ? newValue.subAccountId : null);
                          onPaymentOptionFieldChange(option.id, 'bankAccountName', newValue.bankName ?? null); // ✅ YENİ: Banka adını da kaydet
                        } else {
                          onPaymentOptionFieldChange(option.id, 'bankAccountId', null);
                          onPaymentOptionFieldChange(option.id, 'subAccountId', null);
                          onPaymentOptionFieldChange(option.id, 'bankAccountName', null); // ✅ YENİ: Banka adını da sıfırla
                        }
                      }}
                      renderInput={(params) => <TextField {...params} variant="outlined" size="small" label={t('paymentSettingsTab.labels.posAccount')} />}
                      fullWidth
                      isOptionEqualToValue={(opt: any, val: any) => {
                        if (!opt || !val) return false;
                        if (opt.isSubAccount && val.isSubAccount) return opt.id === val.id;
                        return opt.mainAccountId === val.mainAccountId;
                      }}
                    />
                  )}
                  {option.type === 'openAccount' && (
                    <Typography variant="caption" color="text.secondary">{t('paymentSettingsTab.labels.customerSelectedDuringSale')}</Typography>
                  )}
                  {option.type === 'other' && (
                    <Autocomplete
                      size="small"
                      options={allCashboxes.filter(cb => !paymentOptions.some(opt => opt.id !== option.id && opt.type === 'other' && opt.cashboxId === cb.id))}
                      getOptionLabel={(cb) => cb.name}
                      value={allCashboxes.find(cb => cb.id === option.cashboxId) || null}
                      onChange={(e, newValue) => {
                        onPaymentOptionFieldChange(option.id, 'cashboxId', newValue?.id ?? null);
                        onPaymentOptionFieldChange(option.id, 'cashboxName', newValue?.name ?? null);
                      }}
                      renderInput={(params) => <TextField {...params} variant="outlined" size="small" label={t('paymentSettingsTab.labels.selectCashbox')} />}
                      fullWidth
                    />
                  )}
                  {option.type === 'online' && (
                    // ✅ YENİ: Online ödeme için 3 bağlantı alanı (mobil - dikey)
                    <Stack spacing={1}>
                      {/* 1. Nakit Kasa */}
                      <Autocomplete
                        size="small"
                        options={allCashboxes}
                        getOptionLabel={(cb) => cb.name}
                        value={allCashboxes.find(cb => cb.id === option.onlineCashboxId) || null}
                        onChange={(e, newValue) => {
                          onPaymentOptionFieldChange(option.id, 'onlineCashboxId', newValue?.id ?? null);
                          onPaymentOptionFieldChange(option.id, 'onlineCashboxName', newValue?.name ?? null);
                        }}
                        renderInput={(params) => <TextField {...params} variant="outlined" size="small" label={t('paymentSettingsTab.labels.cashCashbox')} />}
                        fullWidth
                      />
                      {/* 2. POS Hesabı */}
                      <Autocomplete
                        size="small"
                        options={[
                          ...allBankAccounts
                            .filter(ba => ba.isPosAccount)
                            .map(ba => ({
                              id: ba.id,
                              bankName: `${ba.bankName} - ${ba.accountNumber}`,
                              isSubAccount: false,
                              mainAccountId: ba.id,
                              subAccountId: null as string | null
                            })),
                          ...allBankAccounts.flatMap(ba => 
                            ba.subAccounts ? 
                            Object.entries(ba.subAccounts)
                              .filter(([, sub]) => sub.isPosAccount)
                              .map(([subId, sub]) => ({ 
                                id: `${ba.id}_sub_${subId}`,
                                bankName: `${ba.bankName} - ${sub.accountName}`,
                                isSubAccount: true,
                                mainAccountId: ba.id,
                                subAccountId: subId
                              }))
                            : []
                          )
                        ]}
                        getOptionLabel={(opt: any) => opt.bankName}
                        value={(() => {
                          if (option.onlineBankAccountId && option.onlineSubAccountId) {
                            const mainAcc = allBankAccounts.find(ba => ba.id === option.onlineBankAccountId);
                            if (mainAcc) {
                              const subAcc = mainAcc.subAccounts?.[option.onlineSubAccountId];
                              if (subAcc) {
                                return {
                                  id: `${mainAcc.id}_sub_${option.onlineSubAccountId}`,
                                  bankName: `${mainAcc.bankName} - ${subAcc.accountName}`,
                                  isSubAccount: true,
                                  mainAccountId: mainAcc.id,
                                  subAccountId: option.onlineSubAccountId
                                };
                              }
                            }
                          } else if (option.onlineBankAccountId) {
                            const mainAcc = allBankAccounts.find(ba => ba.id === option.onlineBankAccountId);
                            if (mainAcc) {
                              return {
                                id: mainAcc.id,
                                bankName: `${mainAcc.bankName} - ${mainAcc.accountNumber}`,
                                isSubAccount: false,
                                mainAccountId: mainAcc.id,
                                subAccountId: null
                              };
                            }
                          }
                          return null;
                        })()}
                        onChange={(e, newValue: any) => {
                          if (newValue) {
                            onPaymentOptionFieldChange(option.id, 'onlineBankAccountId', newValue.mainAccountId);
                            onPaymentOptionFieldChange(option.id, 'onlineSubAccountId', newValue.isSubAccount ? newValue.subAccountId : null);
                            onPaymentOptionFieldChange(option.id, 'onlineBankAccountName', newValue.bankName ?? null);
                          } else {
                            onPaymentOptionFieldChange(option.id, 'onlineBankAccountId', null);
                            onPaymentOptionFieldChange(option.id, 'onlineSubAccountId', null);
                            onPaymentOptionFieldChange(option.id, 'onlineBankAccountName', null);
                          }
                        }}
                        renderInput={(params) => <TextField {...params} variant="outlined" size="small" label={t('paymentSettingsTab.labels.posAccount')} />}
                        fullWidth
                        isOptionEqualToValue={(opt: any, val: any) => {
                          if (!opt || !val) return false;
                          if (opt.isSubAccount && val.isSubAccount) return opt.id === val.id;
                          return opt.mainAccountId === val.mainAccountId;
                        }}
                      />
                      {/* 3. Online Ödeme için Kasa (şimdilik nakit kasasına kayıt) */}
                      <Autocomplete
                        size="small"
                        options={allCashboxes}
                        getOptionLabel={(cb) => cb.name}
                        value={allCashboxes.find(cb => cb.id === option.onlineDirectCashboxId) || null}
                        onChange={(e, newValue) => {
                          onPaymentOptionFieldChange(option.id, 'onlineDirectCashboxId', newValue?.id ?? null);
                          onPaymentOptionFieldChange(option.id, 'onlineDirectCashboxName', newValue?.name ?? null);
                        }}
                        renderInput={(params) => <TextField {...params} variant="outlined" size="small" label={t('paymentSettingsTab.labels.onlineCashbox')} />}
                        fullWidth
                      />
                    </Stack>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      ) : (
        // 💻 MASAÜSTÜ: Tablo Görünümü
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{fontWeight: 'bold'}}>{t('paymentSettingsTab.table.type')}</TableCell>
                <TableCell sx={{fontWeight: 'bold'}}>{t('paymentSettingsTab.table.labelName')}</TableCell>
                <TableCell sx={{fontWeight: 'bold'}}>{t('paymentSettingsTab.table.connection')}</TableCell>
                <TableCell sx={{fontWeight: 'bold', textAlign: 'center'}}>{t('paymentSettingsTab.table.status')}</TableCell>
                <TableCell sx={{fontWeight: 'bold', textAlign: 'right'}}>{t('paymentSettingsTab.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paymentOptions.map((option) => {
                const usedCashboxIds = paymentOptions
                  .filter(opt => opt.id !== option.id && opt.type === 'cash' && opt.cashboxId)
                  .map(opt => opt.cashboxId);
                const usedBankAccountIds = paymentOptions
                  .filter(opt => opt.id !== option.id && opt.type === 'pos' && opt.bankAccountId)
                  .map(opt => opt.bankAccountId);
                
                let typeLabel = '';
                switch (option.type) {
                  case 'cash': typeLabel = t('paymentSettingsTab.paymentTypes.cash'); break;
                  case 'pos': typeLabel = 'POS'; break;
                  case 'openAccount': typeLabel = t('paymentSettingsTab.paymentTypes.openAccount'); break;
                  case 'other': typeLabel = t('paymentSettingsTab.paymentTypes.other'); break;
                  case 'online': typeLabel = 'Online'; break;
                  default: typeLabel = option.type;
                }
                
                return (
                  <TableRow key={option.id}>
                    <TableCell sx={{textTransform: 'capitalize'}}>{typeLabel}</TableCell>
                    <TableCell>
                      {option.type === 'other' ? (
                        // ✅ YENİ: Diğer ödeme için hardcoded dropdown
                        <Autocomplete
                          size="small"
                          options={OTHER_PAYMENT_OPTIONS}
                          value={OTHER_PAYMENT_OPTIONS.includes(option.label) ? option.label : undefined}
                          onChange={(e, newValue) => onPaymentOptionFieldChange(option.id, 'label', newValue || '')}
                          renderInput={(params) => <TextField {...params} variant="standard" placeholder={t('paymentSettingsTab.placeholders.selectPaymentType')} />}
                          sx={{ minWidth: 150 }}
                        />
                      ) : option.type === 'online' ? (
                        // ✅ YENİ: Online ödeme için hardcoded dropdown
                        <Autocomplete
                          size="small"
                          options={ONLINE_PAYMENT_OPTIONS}
                          value={ONLINE_PAYMENT_OPTIONS.includes(option.label) ? option.label : undefined}
                          onChange={(e, newValue) => onPaymentOptionFieldChange(option.id, 'label', newValue || '')}
                          renderInput={(params) => <TextField {...params} variant="standard" placeholder={t('paymentSettingsTab.placeholders.selectOnlinePlatform')} />}
                          sx={{ minWidth: 150 }}
                        />
                      ) : (
                        <TextField 
                          variant="standard"
                          size="small"
                          fullWidth
                          value={option.label}
                          onChange={(e) => onPaymentOptionFieldChange(option.id, 'label', e.target.value)}
                          sx={{ minWidth: 150 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {option.type === 'cash' && (
                        <Autocomplete
                          size="small"
                          options={allCashboxes.filter(cb => 
                            !usedCashboxIds.includes(cb.id) || cb.id === option.cashboxId
                          )}
                          getOptionLabel={(cb) => cb.name}
                          value={allCashboxes.find(cb => cb.id === option.cashboxId) || null}
                          onChange={(e, newValue) => {
                            onPaymentOptionFieldChange(option.id, 'cashboxId', newValue?.id ?? null);
                            onPaymentOptionFieldChange(option.id, 'cashboxName', newValue?.name ?? null); // ✅ YENİ: Kasa adını da kaydet
                          }}
                          renderInput={(params) => <TextField {...params} variant="standard" placeholder={t('paymentSettingsTab.placeholders.selectCashbox')} />}
                          sx={{ minWidth: 180 }}
                        />
                      )}
                      {option.type === 'pos' && (
                        <Autocomplete
                          size="small"
                          options={[
                            // POS özellikli ana hesaplar
                            ...allBankAccounts
                              .filter(ba => ba.isPosAccount && (!usedBankAccountIds.includes(ba.id) || ba.id === option.bankAccountId))
                              .map(ba => ({
                                id: ba.id,
                                bankName: `${ba.bankName} - ${ba.accountNumber}`,
                                accountNumber: ba.accountNumber,
                                isPosAccount: true,
                                isSubAccount: false,
                                mainAccountId: ba.id,
                                subAccountId: null
                              })),
                            // POS özellikli alt hesaplar
                            ...allBankAccounts.flatMap(ba => 
                              ba.subAccounts ? 
                              Object.entries(ba.subAccounts)
                                .filter(([subId, sub]) => sub.isPosAccount)
                                .map(([subId, sub]) => ({ 
                                  id: `${ba.id}_sub_${subId}`,
                                  bankName: `${ba.bankName} - ${sub.accountName}`,
                                  accountNumber: sub.accountNumber,
                                  isPosAccount: true,
                                  isSubAccount: true,
                                  mainAccountId: ba.id,
                                  subAccountId: subId
                                }))
                              : []
                            )
                          ]}
                          getOptionLabel={(opt: any) => opt.bankName}
                          value={(() => {
                            if (option.bankAccountId && option.subAccountId) {
                              // Seçili bir alt hesap varsa onu bul
                              const mainAcc = allBankAccounts.find(ba => ba.id === option.bankAccountId);
                              if (!mainAcc) {
                                console.warn(`Ana hesap bulunamadı: ${option.bankAccountId}`);
                                return null;
                              }
                              
                              const subAcc = mainAcc.subAccounts?.[option.subAccountId];
                              if (mainAcc && subAcc) {
                                return {
                                  id: `${mainAcc.id}_sub_${option.subAccountId}`,
                                  bankName: `${mainAcc.bankName} - ${subAcc.accountName}`,
                                  accountNumber: subAcc.accountNumber,
                                  isPosAccount: true,
                                  isSubAccount: true,
                                  mainAccountId: mainAcc.id,
                                  subAccountId: option.subAccountId
                                };
                              }
                            } else if (option.bankAccountId) {
                              // Sadece ana hesap seçiliyse onu bul
                              const mainAcc = allBankAccounts.find(ba => ba.id === option.bankAccountId);
                              if (mainAcc) {
                                return {
                                  id: mainAcc.id,
                                  bankName: `${mainAcc.bankName} - ${mainAcc.accountNumber}`,
                                  accountNumber: mainAcc.accountNumber,
                                  isPosAccount: true,
                                  isSubAccount: false,
                                  mainAccountId: mainAcc.id,
                                  subAccountId: null
                                };
                              }
                            }
                            return null;
                          })()}
                          onChange={(e, newValue: any) => {
                            console.log("Seçilen banka hesabı:", newValue);
                            if (newValue) {
                              // Ana hesabı ve alt hesabı ayarla
                              onPaymentOptionFieldChange(option.id, 'bankAccountId', newValue.mainAccountId);
                              onPaymentOptionFieldChange(option.id, 'subAccountId', newValue.isSubAccount ? newValue.subAccountId : null);
                              onPaymentOptionFieldChange(option.id, 'bankAccountName', newValue.bankName ?? null); // ✅ YENİ: Banka adını da kaydet
                            } else {
                              // Değerler null olarak temizlenir
                              onPaymentOptionFieldChange(option.id, 'bankAccountId', null);
                              onPaymentOptionFieldChange(option.id, 'subAccountId', null);
                              onPaymentOptionFieldChange(option.id, 'bankAccountName', null); // ✅ YENİ: Banka adını da sıfırla
                            }
                          }}
                          renderInput={(params) => <TextField {...params} variant="standard" placeholder={t('paymentSettingsTab.placeholders.selectPosAccount')} />}
                          sx={{ minWidth: 220 }}
                          isOptionEqualToValue={(opt: any, val: any) => {
                            if (!opt || !val) return false;
                            if (opt.isSubAccount && val.isSubAccount) {
                              return opt.id === val.id; // Alt hesap karşılaştırması
                            }
                            return opt.mainAccountId === val.mainAccountId; // Ana hesap karşılaştırması
                          }}
                        />
                      )}
                      {option.type === 'openAccount' && (
                        <Typography variant="caption" color="text.secondary">-</Typography>
                      )}
                      {option.type === 'other' && (
                        <Autocomplete
                          size="small"
                          options={allCashboxes.filter(cb => !paymentOptions.some(opt => opt.id !== option.id && opt.type === 'other' && opt.cashboxId === cb.id))}
                          getOptionLabel={(cb) => cb.name}
                          value={allCashboxes.find(cb => cb.id === option.cashboxId) || null}
                          onChange={(e, newValue) => {
                            onPaymentOptionFieldChange(option.id, 'cashboxId', newValue?.id ?? null);
                            onPaymentOptionFieldChange(option.id, 'cashboxName', newValue?.name ?? null);
                          }}
                          renderInput={(params) => <TextField {...params} variant="standard" placeholder={t('paymentSettingsTab.placeholders.selectCashbox')} />}
                          sx={{ minWidth: 180 }}
                        />
                      )}
                      {option.type === 'online' && (
                        // ✅ YENİ: Online ödeme için 3 bağlantı alanı yan yana
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          {/* 1. Nakit Kasa Seçimi */}
                          <Autocomplete
                            size="small"
                            options={allCashboxes}
                            getOptionLabel={(cb) => cb.name}
                            value={allCashboxes.find(cb => cb.id === option.onlineCashboxId) || null}
                            onChange={(e, newValue) => {
                              onPaymentOptionFieldChange(option.id, 'onlineCashboxId', newValue?.id ?? null);
                              onPaymentOptionFieldChange(option.id, 'onlineCashboxName', newValue?.name ?? null);
                            }}
                            renderInput={(params) => <TextField {...params} variant="standard" placeholder={t('paymentSettingsTab.placeholders.cashCashbox')} />}
                            sx={{ minWidth: 120 }}
                          />
                          {/* 2. POS Banka Seçimi */}
                          <Autocomplete
                            size="small"
                            options={[
                              ...allBankAccounts
                                .filter(ba => ba.isPosAccount)
                                .map(ba => ({
                                  id: ba.id,
                                  bankName: `${ba.bankName} - ${ba.accountNumber}`,
                                  isSubAccount: false,
                                  mainAccountId: ba.id,
                                  subAccountId: null as string | null
                                })),
                              ...allBankAccounts.flatMap(ba => 
                                ba.subAccounts ? 
                                Object.entries(ba.subAccounts)
                                  .filter(([, sub]) => sub.isPosAccount)
                                  .map(([subId, sub]) => ({ 
                                    id: `${ba.id}_sub_${subId}`,
                                    bankName: `${ba.bankName} - ${sub.accountName}`,
                                    isSubAccount: true,
                                    mainAccountId: ba.id,
                                    subAccountId: subId
                                  }))
                                : []
                              )
                            ]}
                            getOptionLabel={(opt: any) => opt.bankName}
                            value={(() => {
                              if (option.onlineBankAccountId && option.onlineSubAccountId) {
                                const mainAcc = allBankAccounts.find(ba => ba.id === option.onlineBankAccountId);
                                if (mainAcc) {
                                  const subAcc = mainAcc.subAccounts?.[option.onlineSubAccountId];
                                  if (subAcc) {
                                    return {
                                      id: `${mainAcc.id}_sub_${option.onlineSubAccountId}`,
                                      bankName: `${mainAcc.bankName} - ${subAcc.accountName}`,
                                      isSubAccount: true,
                                      mainAccountId: mainAcc.id,
                                      subAccountId: option.onlineSubAccountId
                                    };
                                  }
                                }
                              } else if (option.onlineBankAccountId) {
                                const mainAcc = allBankAccounts.find(ba => ba.id === option.onlineBankAccountId);
                                if (mainAcc) {
                                  return {
                                    id: mainAcc.id,
                                    bankName: `${mainAcc.bankName} - ${mainAcc.accountNumber}`,
                                    isSubAccount: false,
                                    mainAccountId: mainAcc.id,
                                    subAccountId: null
                                  };
                                }
                              }
                              return null;
                            })()}
                            onChange={(e, newValue: any) => {
                              if (newValue) {
                                onPaymentOptionFieldChange(option.id, 'onlineBankAccountId', newValue.mainAccountId);
                                onPaymentOptionFieldChange(option.id, 'onlineSubAccountId', newValue.isSubAccount ? newValue.subAccountId : null);
                                onPaymentOptionFieldChange(option.id, 'onlineBankAccountName', newValue.bankName ?? null);
                              } else {
                                onPaymentOptionFieldChange(option.id, 'onlineBankAccountId', null);
                                onPaymentOptionFieldChange(option.id, 'onlineSubAccountId', null);
                                onPaymentOptionFieldChange(option.id, 'onlineBankAccountName', null);
                              }
                            }}
                            renderInput={(params) => <TextField {...params} variant="standard" placeholder="POS" />}
                            sx={{ minWidth: 140 }}
                            isOptionEqualToValue={(opt: any, val: any) => {
                              if (!opt || !val) return false;
                              if (opt.isSubAccount && val.isSubAccount) return opt.id === val.id;
                              return opt.mainAccountId === val.mainAccountId;
                            }}
                          />
                          {/* 3. Online Ödeme için Kasa (şimdilik nakit kasasına kayıt) */}
                          <Autocomplete
                            size="small"
                            options={allCashboxes}
                            getOptionLabel={(cb) => cb.name}
                            value={allCashboxes.find(cb => cb.id === option.onlineDirectCashboxId) || null}
                            onChange={(e, newValue) => {
                              onPaymentOptionFieldChange(option.id, 'onlineDirectCashboxId', newValue?.id ?? null);
                              onPaymentOptionFieldChange(option.id, 'onlineDirectCashboxName', newValue?.name ?? null);
                            }}
                            renderInput={(params) => <TextField {...params} variant="standard" placeholder={t('paymentSettingsTab.placeholders.onlineCashbox')} />}
                            sx={{ minWidth: 120 }}
                          />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        size="small"
                        checked={option.enabled}
                        onChange={(e) => onPaymentOptionFieldChange(option.id, 'enabled', e.target.checked)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" color="error" onClick={() => onDeletePaymentOption(option.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      

      <PavoPaySettingsModal
        open={pavoPaySettingsModalOpen}
        onClose={() => setPavoPaySettingsModalOpen(false)}
      />

      <PavoP2PDevicesModal
        open={pavoP2pModalOpen}
        onClose={() => setPavoP2pModalOpen(false)}
        companyId={companyId}
        branchId={branchId}
      />

      <PavoRestP2PDeviceSelectDialog
        open={restP2PSelectOpen}
        onClose={() => setRestP2PSelectOpen(false)}
        companyId={companyId}
        branchId={branchId}
        currentDeviceCode={pavoRestP2PDeviceScreenCode}
        onSelect={(device: PavoDevice) => {
          onPavoRestP2PDeviceChange({
            code: device.deviceCode,
            name: device.deviceName || device.deviceCode,
          });
        }}
      />

      {/* Ingenico Cihaz Departman-KDV Tablosu Modalı */}
      <IngenicoDeviceDepartmentsModal
        open={ingenicoDeviceDeptModalOpen}
        onClose={() => setIngenicoDeviceDeptModalOpen(false)}
      />
    </Paper>
  );
};

export default PaymentSettingsTab; 